import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ClientService} from '../auth/client.service';
import {
    HubspotIntegrationStatus,
    WebinarChatModerationAction,
    WebinarChatModerationResult,
    HubspotWebinarsValidationResult,
    Webinar,
    WebinarAnalyticsResponse,
    WebinarChatMessage,
    WebinarEngagementInput,
    WebinarEngagementItem,
    WebinarInput,
    WebinarRegistration,
    WebinarReportFilters,
    WebinarReportResponse,
    WebinarSeries,
    WebinarSeriesCreateResponse,
    WebinarSeriesInput,
    WebinarSeriesListResponse,
    WebinarSeriesPreviewResponse,
    WebinarSettings,
    WebinarSettingsInput,
} from '../../models/webinar.model';

@Injectable({
    providedIn: 'root',
})
export class WebinarsService {
    private http = inject(HttpClient);
    private clientService = inject(ClientService);

    private readonly baseUrl = environment.webinarsServerUrl.replace(/\/$/, '');
    private readonly adminApiUrl = `${this.baseUrl}/admin/api`;

    private requireClientId(): string {
        const clientId = this.clientService.client?.id;
        if (!clientId) {
            throw new Error('client_not_selected');
        }
        return clientId;
    }

    private buildHeaders(clientId: string): HttpHeaders {
        return new HttpHeaders({
            'x-client-id': clientId,
        });
    }

    private appendParam(params: HttpParams, name: string, value: string | number | boolean | null | undefined): HttpParams {
        if (value === null || value === undefined || value === '') {
            return params;
        }
        return params.set(name, String(value));
    }

    getWebinars(): Observable<Webinar[]> {
        try {
            const clientId = this.requireClientId();
            return this.http.get<Webinar[]>(`${this.adminApiUrl}/webinars`, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    getWebinar(id: string, expectedClientId?: string | null): Observable<Webinar> {
        try {
            const clientId = this.requireClientId();
            if (expectedClientId && expectedClientId !== clientId) {
                throw new Error('webinar_history_account_mismatch');
            }
            return this.http.get<Webinar>(`${this.adminApiUrl}/webinars/${encodeURIComponent(id)}`, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    createWebinar(payload: WebinarInput): Observable<Webinar> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<Webinar>(`${this.adminApiUrl}/webinars`, payload, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    previewWebinarSeries(payload: WebinarSeriesInput): Observable<WebinarSeriesPreviewResponse> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<WebinarSeriesPreviewResponse>(`${this.adminApiUrl}/webinar-series/preview`, payload, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    createWebinarSeries(payload: WebinarSeriesInput): Observable<WebinarSeriesCreateResponse> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<WebinarSeriesCreateResponse>(`${this.adminApiUrl}/webinar-series`, payload, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    getWebinarSeriesList(options: {limit?: number | null; offset?: number | null} = {}): Observable<WebinarSeriesListResponse> {
        try {
            const clientId = this.requireClientId();
            let params = new HttpParams();
            params = this.appendParam(params, 'limit', options.limit);
            params = this.appendParam(params, 'offset', options.offset);
            return this.http.get<WebinarSeriesListResponse>(`${this.adminApiUrl}/webinar-series`, {
                headers: this.buildHeaders(clientId),
                params,
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    getWebinarSeries(id: string, options: {limit?: number | null; offset?: number | null} = {}): Observable<WebinarSeries> {
        try {
            const clientId = this.requireClientId();
            let params = new HttpParams();
            params = this.appendParam(params, 'limit', options.limit);
            params = this.appendParam(params, 'offset', options.offset);
            return this.http.get<WebinarSeries>(`${this.adminApiUrl}/webinar-series/${encodeURIComponent(id)}`, {
                headers: this.buildHeaders(clientId),
                params,
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    updateWebinar(id: string, payload: WebinarInput): Observable<Webinar> {
        try {
            const clientId = this.requireClientId();
            return this.http.put<Webinar>(`${this.adminApiUrl}/webinars/${encodeURIComponent(id)}`, payload, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    deleteWebinar(id: string): Observable<void> {
        try {
            const clientId = this.requireClientId();
            return this.http.delete<void>(`${this.adminApiUrl}/webinars/${encodeURIComponent(id)}`, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    getRegistrations(id: string): Observable<WebinarRegistration[]> {
        try {
            const clientId = this.requireClientId();
            return this.http.get<WebinarRegistration[]>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/registrations`,
                {
                    headers: this.buildHeaders(clientId),
                },
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    getWebinarAnalytics(
        id: string,
        options: {
            includeUsers?: boolean;
            includeUserPii?: boolean;
            limit?: number | null;
            cursor?: string | null;
        } = {},
    ): Observable<WebinarAnalyticsResponse> {
        try {
            const clientId = this.requireClientId();
            let params = new HttpParams();
            params = this.appendParam(params, 'includeUsers', options.includeUsers === true ? 'true' : null);
            params = this.appendParam(params, 'includeUserPii', options.includeUserPii === true ? 'true' : null);
            params = this.appendParam(params, 'limit', options.limit);
            params = this.appendParam(params, 'cursor', options.cursor);
            return this.http.get<WebinarAnalyticsResponse>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/analytics`,
                {
                    headers: this.buildHeaders(clientId),
                    params,
                },
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    getWebinarReport(filters: WebinarReportFilters = {}): Observable<WebinarReportResponse> {
        try {
            const clientId = this.requireClientId();
            let params = new HttpParams();
            params = this.appendParam(params, 'from', filters.from);
            params = this.appendParam(params, 'to', filters.to);
            params = this.appendParam(params, 'status', filters.status);
            params = this.appendParam(params, 'provider', filters.provider);
            params = this.appendParam(params, 'accessMode', filters.accessMode);
            params = this.appendParam(params, 'limit', filters.limit);
            params = this.appendParam(params, 'cursor', filters.cursor);
            return this.http.get<WebinarReportResponse>(`${this.adminApiUrl}/webinars/report`, {
                headers: this.buildHeaders(clientId),
                params,
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    getWebinarChats(id: string): Observable<WebinarChatMessage[]> {
        try {
            const clientId = this.requireClientId();
            return this.http.get<WebinarChatMessage[]>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/chats`,
                {
                    headers: this.buildHeaders(clientId),
                },
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    moderateChatMessage(
        id: string,
        messageId: string,
        action: WebinarChatModerationAction,
    ): Observable<WebinarChatModerationResult> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<WebinarChatModerationResult>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/chats/${encodeURIComponent(messageId)}/moderation`,
                {action},
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    getHubspotStatus(): Observable<HubspotIntegrationStatus> {
        try {
            const clientId = this.requireClientId();
            return this.http.get<HubspotIntegrationStatus>(`${this.adminApiUrl}/hubspot/status`, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    validateHubspotWebinars(): Observable<HubspotWebinarsValidationResult> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<HubspotWebinarsValidationResult>(
                `${this.adminApiUrl}/hubspot/validate`,
                {},
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    provisionHubspotWebinars(): Observable<HubspotWebinarsValidationResult> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<HubspotWebinarsValidationResult>(
                `${this.adminApiUrl}/hubspot/provision`,
                {},
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    getWebinarSettings(): Observable<WebinarSettings> {
        try {
            const clientId = this.requireClientId();
            return this.http.get<WebinarSettings>(`${this.adminApiUrl}/webinars/settings`, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    updateWebinarSettings(payload: WebinarSettingsInput): Observable<WebinarSettings> {
        try {
            const clientId = this.requireClientId();
            return this.http.put<WebinarSettings>(`${this.adminApiUrl}/webinars/settings`, payload, {
                headers: this.buildHeaders(clientId),
            });
        } catch (err) {
            return throwError(() => err);
        }
    }

    getEngagements(id: string): Observable<WebinarEngagementItem[]> {
        try {
            const clientId = this.requireClientId();
            return this.http.get<WebinarEngagementItem[]>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/engagements`,
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    createEngagement(id: string, payload: WebinarEngagementInput): Observable<WebinarEngagementItem> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<WebinarEngagementItem>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/engagements`,
                payload,
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    updateEngagement(
        id: string,
        engagementId: string,
        payload: WebinarEngagementInput,
    ): Observable<WebinarEngagementItem> {
        try {
            const clientId = this.requireClientId();
            return this.http.put<WebinarEngagementItem>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/engagements/${encodeURIComponent(engagementId)}`,
                payload,
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    deleteEngagement(id: string, engagementId: string): Observable<void> {
        try {
            const clientId = this.requireClientId();
            return this.http.delete<void>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/engagements/${encodeURIComponent(engagementId)}`,
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    publishEngagement(id: string, engagementId: string): Observable<WebinarEngagementItem> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<WebinarEngagementItem>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/engagements/${encodeURIComponent(engagementId)}/publish`,
                {},
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }

    closeEngagement(id: string, engagementId: string): Observable<WebinarEngagementItem> {
        try {
            const clientId = this.requireClientId();
            return this.http.post<WebinarEngagementItem>(
                `${this.adminApiUrl}/webinars/${encodeURIComponent(id)}/engagements/${encodeURIComponent(engagementId)}/close`,
                {},
                {headers: this.buildHeaders(clientId)},
            );
        } catch (err) {
            return throwError(() => err);
        }
    }
}
